import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { notFound } from '@tanstack/react-router';
import { Check, ChevronRight } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  Body,
  Button,
  Caption,
  Card,
  CardGrid,
  EmptyState,
  Heading,
  I,
  Icon,
  Pill,
  Row,
  ScreenBody,
  Skeleton,
  SkeletonGroup,
  SkeletonText,
  Stack,
  TONE,
  useDelayedLoading,
} from '@leanin/ui';

import { RouterLinkAs } from '@/lib/router-link';
import { useMember } from '@/lib/use-session';
import { toneFromId } from '@/lib/group-tone';
import { TRACK_TONES } from './LearningTrackCard';
import styles from './LearningTrackDetailView.module.css';
import {
  useLearningTrack,
  type LearningTrackDetail,
  type LearningTrackItem,
} from '@/lib/queries/learning-tracks';
import { useSetTrackItemProgress } from '@/lib/mutations/learning-tracks';
import { Toggle } from '@leanin/ui/client';

// Curriculum view of a learning track: hero (cover + badge + title + start),
// viewer progress bar, then the items as a numbered sequence — thumbnail,
// minutes, title, lede, a completion check (signed-in only), and a
// chevron into the resource itself.

type Props = {
  readonly trackId: string;
  readonly canManage: boolean;
};

const layersChip = (
  <svg aria-hidden viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
    <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
    <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
  </svg>
);

export function LearningTrackDetailView({ trackId, canManage }: Props) {
  const t = useTranslations('LearningTracks');
  const trackQuery = useLearningTrack(trackId, { retry: false, retryOnMount: false });
  const signedIn = useMember() !== null;

  const isLoading = trackQuery.isPending || trackQuery.data === undefined;
  const showSkeleton = useDelayedLoading(isLoading);
  // 404 and 410 GONE (public track since deleted) → branded NotFoundPage
  // directly. Other errors fall through.
  if (isNotFoundApiError(trackQuery.error)) return <NotFoundPage />;
  if (trackQuery.isError) throw notFound();
  if (showSkeleton) {
    return <LearningTrackDetailSkeleton />;
  }
  if (isLoading) return null;

  const track = trackQuery.data;

  return (
    <ScreenBody
      gap="none"
      style={{ background: 'var(--background)', minHeight: '100%' }}
    >
      <Stack gap="xl" pt="lg">
        <Row gap="sm" align="center" justify="space-between">
          <Button
            href="/resources"
            linkAs={RouterLinkAs}
            variant="secondary"
            size="sm"
            icon={I.back}
          >
            {t('detail.back')}
          </Button>
          <Row gap="sm" align="center">
            {!track.published ? (
              <Pill tone="neutral">{t('detail.draft')}</Pill>
            ) : null}
            {canManage ? (
              <Button
                href={`/admin/resources/learning-tracks/${track.id}`}
                linkAs={RouterLinkAs}
                variant="secondary"
                size="sm"
                icon={I.edit}
              >
                {t('detail.manage')}
              </Button>
            ) : null}
          </Row>
        </Row>

        <TrackHero track={track} />

        {signedIn && track.items.length > 0 ? (
          <ProgressBar
            completed={track.viewerCompletedCount}
            total={track.items.length}
          />
        ) : null}

        <TrackItems track={track} signedIn={signedIn} />
      </Stack>
    </ScreenBody>
  );
}

function TrackHero({ track }: { readonly track: LearningTrackDetail }) {
  const t = useTranslations('LearningTracks');
  // Same photo the preview card leads with: the explicit cover, falling
  // back to the first item's.
  const cover =
    track.coverImageUrl ??
    track.items.find((i) => i.resource?.coverImageUrl != null)?.resource
      ?.coverImageUrl ??
    null;
  const tone = TONE[toneFromId(track.id, TRACK_TONES)];

  return (
    <div
      className={styles.hero}
      style={{ '--hero-tone': tone.bg } as React.CSSProperties}
    >
      {cover !== null ? (
        <img className={styles.heroImage} src={cover} alt="" />
      ) : null}
      <div className={styles.heroScrim} />
      <Stack gap="md" align="flex-start" className={styles.heroContent}>
        <span className={styles.heroBadge}>
          {layersChip}
          <span>{t('card.badge')}</span>
        </span>
        <Heading level={1} style={{ color: '#fff' }}>
          {track.title}
        </Heading>
        {track.description.length > 0 ? (
          <Body style={{ color: 'rgba(255,255,255,0.85)' }}>
            {track.description}
          </Body>
        ) : null}
        <Caption style={{ color: 'rgba(255,255,255,0.85)' }}>
          {t('card.resourceCount', { count: track.resourceCount })}
        </Caption>
      </Stack>
    </div>
  );
}

function ProgressBar({
  completed,
  total,
}: {
  readonly completed: number;
  readonly total: number;
}) {
  const t = useTranslations('LearningTracks');
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <Row gap="lg" align="center">
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={completed}
        aria-label={t('detail.progressAria')}
      >
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
      <Caption>
        {t('detail.progressLabel', { completed, total })}
      </Caption>
    </Row>
  );
}

function TrackItems({
  track,
  signedIn,
}: {
  readonly track: LearningTrackDetail;
  readonly signedIn: boolean;
}) {
  const t = useTranslations('LearningTracks');
  const ordered = useMemo(
    () => [...track.items].sort((a, b) => a.sortOrder - b.sortOrder),
    [track.items],
  );

  if (ordered.length === 0) {
    return (
      <Card pad="flush" style={{ overflow: 'hidden' }}>
        <EmptyState
          density="page"
          tone="burgundy"
          icon="book"
          eyebrow={t('detail.emptyEyebrow')}
          title={t('detail.emptyTitle')}
          body={t('detail.emptyBody')}
        />
      </Card>
    );
  }

  return (
    <Stack gap="none">
      {ordered.map((item, index) => (
        <TrackItemRow
          key={item.resourceId}
          trackId={track.id}
          item={item}
          position={index + 1}
          signedIn={signedIn}
        />
      ))}
    </Stack>
  );
}

function TrackItemRow({
  trackId,
  item,
  position,
  signedIn,
}: {
  readonly trackId: string;
  readonly item: LearningTrackItem;
  readonly position: number;
  readonly signedIn: boolean;
}) {
  const t = useTranslations('LearningTracks');
  const navigate = useNavigate();
  const progress = useSetTrackItemProgress(trackId);

  if (item.resource === null) {
    return (
      <div className={styles.itemRow}>
        <span className={styles.itemNumber}>{position}</span>
        <Stack gap="xs" style={{ flex: 1, minWidth: 0, opacity: 0.7 }}>
          <Caption>{t('detail.removed')}</Caption>
          <Body size="sm" color="mute">
            {t('detail.removedBody')}
          </Body>
        </Stack>
      </div>
    );
  }

  const resource = item.resource;

  return (
    // Whole-row click opens the resource (the chevron stays as the
    // accessible link); nested controls stop propagation.
    <div
      className={styles.itemRow}
      data-clickable
      onClick={() => void navigate({ to: `/resources/${resource.id}` })}
    >
      <span className={styles.itemNumber}>{position}</span>
      <div
        className={styles.itemThumb}
        style={
          {
            '--thumb-tone': TONE[toneFromId(resource.id, TRACK_TONES)].bg,
          } as React.CSSProperties
        }
      >
        {resource.coverImageUrl !== null ? (
          <img src={resource.coverImageUrl} alt="" />
        ) : null}
      </div>
      <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
        {/* eslint-disable-next-line @leanin/ui/typography-component-required -- same semibold title exception as the preview cards */}
        <h3 className={styles.itemTitle}>{resource.title}</h3>
        {resource.lede !== null && resource.lede.length > 0 ? (
          <Body size="sm" color="mute" className={styles.itemLede}>
            {resource.lede}
          </Body>
        ) : null}
      </Stack>
      {/* Nested controls own their clicks — keep the row-click handler
          from also firing under the check / chevron. */}
      <Row gap="sm" align="center" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        {signedIn ? (
          <Toggle
            pressed={item.viewerCompleted}
            onPressedChange={(next) =>
              progress.mutate({ resourceId: item.resourceId, completed: next })
            }
            disabled={progress.isPending}
            className={styles.checkButton}
            data-completed={item.viewerCompleted}
            aria-label={
              item.viewerCompleted
                ? t('detail.markIncompleteAria', { title: resource.title })
                : t('detail.markCompleteAria', { title: resource.title })
            }
          >
            {item.viewerCompleted ? (
              <>
                <Icon size="sm">
                  <Check size="100%" strokeWidth={2} />
                </Icon>
                {t('detail.completed')}
              </>
            ) : (
              t('detail.markComplete')
            )}
          </Toggle>
        ) : null}
        <Button
          href={`/resources/${resource.id}`}
          linkAs={RouterLinkAs}
          variant="secondary"
          size="md"
          icon={<ChevronRight size="100%" strokeWidth={1.75} />}
          aria-label={t('detail.openResourceAria', { title: resource.title })}
        />
      </Row>
    </div>
  );
}

export function LearningTrackDetailSkeleton() {
  const t = useTranslations('LearningTracks');
  return (
    <SkeletonGroup label={t('detail.loading')}>
      <ScreenBody
        gap="none"
        style={{ background: 'var(--background)', minHeight: '100%' }}
      >
        <Stack gap="xl" pt="lg">
          <Skeleton width="100%" height={260} rounded="lg" />
          <Skeleton width="100%" height={8} rounded="full" />
          <CardGrid gap="lg">
            {Array.from({ length: 4 }, (_, i) => (
              <Card key={i} pad="default" direction="column">
                <Stack gap="md">
                  <Skeleton width={64} height={20} rounded="sm" />
                  <Skeleton width="80%" height={24} rounded="sm" />
                  <SkeletonText lines={2} size="md" />
                </Stack>
              </Card>
            ))}
          </CardGrid>
        </Stack>
      </ScreenBody>
    </SkeletonGroup>
  );
}
